import React, { useState } from 'react';
import { Card, CardBody, Chip, Select, SelectItem, Tooltip } from '@nextui-org/react';
import {
  COHORT,
  RULE,
  DISTRIBUTION,
  DESTINATIONS,
  DESTINATIONS_NOTE,
  WEEK_CHART,
  VARIETIES,
  VARIETIES_NOTE,
  VARIETY_WEEK_CHIPS,
  STRIPS,
  PLANTS,
  CARRIERS,
  RECEIVERS,
  METHODOLOGY,
  ARRIVAL_CHART,
  STAGE_COLORS,
  STATUS_COLORS,
  type PercRow,
} from './mock';
import { WeekIndexChart, ArrivalDeviationChart } from './charts';

/* ---------- primitivas (patrones de la plataforma) ---------- */

/** Header de card estilo Evolución/Retail: título bold + meta a la derecha. */
const CardHead = ({ title, meta, sub }: { title: string; meta?: string; sub?: string }) => (
  <div className='mb-2'>
    <div className='flex flex-col sm:flex-row justify-between sm:items-center gap-1'>
      <h3 className='text-lg font-bold'>{title}</h3>
      {meta && <p className='text-sm text-default-500'>{meta}</p>}
    </div>
    {sub && <p className='text-sm text-default-500 max-w-[75ch]'>{sub}</p>}
  </div>
);

const ChartNote = ({ children }: { children: React.ReactNode }) => (
  <p className='text-xs text-default-400 mt-3 pt-3 border-t border-default-100'>{children}</p>
);

const gapClass = (gap: string) =>
  gap.startsWith('+') ? 'text-danger' : gap.startsWith('−') ? 'text-success-600' : 'text-default-400';

const TagChip = ({ tag }: { tag: { label: string; warn?: boolean } }) => (
  <Chip
    size='sm'
    variant='bordered'
    className='ml-1.5 h-5'
    classNames={{
      content: `text-[10px] px-0.5 ${tag.warn ? 'text-warning-600' : 'text-default-400'}`,
      base: tag.warn ? 'border-warning-400' : 'border-default-200',
    }}
  >
    {tag.label}
  </Chip>
);

/** Stat tile como los del resumen en Despachos → Detalle. */
const StatTile = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
  <div className='rounded-lg border border-default-200 bg-default-50/60 px-3 py-2'>
    <p className='text-xs text-default-400'>{label}</p>
    <p className='text-sm font-semibold text-default-700 tabular-nums'>{value}</p>
    {hint && <p className='text-[11px] text-default-400'>{hint}</p>}
  </div>
);

const PercTrack = ({ row, height = 22 }: { row: PercRow; height?: number }) => (
  <div className='relative rounded-md border border-default-100 bg-default-50' style={{ height }}>
    <div className='absolute top-[5px] bottom-[5px] bg-default-200 rounded-[2px]' style={{ left: `${row.iqrLeft}%`, width: `${row.iqrWidth}%` }} />
    <div className='absolute top-[2px] bottom-[2px] w-[2px] bg-default-400' style={{ left: `${row.median}%` }} />
    {row.threshold != null && (
      <div className='absolute -top-1 -bottom-1 w-px opacity-50' style={{ left: `${row.threshold}%`, background: STATUS_COLORS.critico }} />
    )}
    <div className='absolute -top-1 -bottom-1 w-[3px] bg-primary z-[2]' style={{ left: `${row.you}%` }}>
      <span className='absolute -top-[19px] left-1/2 -translate-x-1/2 tabular-nums text-[10.5px] bg-primary text-white px-1 rounded whitespace-nowrap'>
        {row.youLabel}
      </span>
    </div>
  </div>
);

const PercRowLine = ({ row }: { row: PercRow }) => (
  <div className='grid grid-cols-[150px_1fr_78px] max-md:grid-cols-[104px_1fr_56px] gap-3.5 items-center py-2.5 border-b border-default-100 last:border-none'>
    <div className='text-[13.5px] font-medium leading-tight'>
      {row.name}
      {row.tag && <TagChip tag={row.tag} />}
      <span className='block tabular-nums text-[11px] text-default-400 font-normal mt-0.5'>{row.sub}</span>
    </div>
    <PercTrack row={row} />
    <div className={`tabular-nums text-[13px] font-medium text-right ${gapClass(row.gap)}`}>{row.gap}</div>
  </div>
);

const AxisRow = ({ labels, spread = false }: { labels: string[]; spread?: boolean }) => (
  <div className={`flex tabular-nums text-[10.5px] text-default-400 mt-2 ${spread ? '' : 'justify-between'}`}>
    {labels.map((l, i) =>
      spread ? (
        <span key={i} className={`flex-1 ${i === 0 ? 'text-left' : i === labels.length - 1 ? 'text-right' : 'text-center'}`}>{l}</span>
      ) : (
        <span key={i}>{l}</span>
      )
    )}
  </div>
);

const ZoomHead = ({ badge, title, right }: { badge: string; title: string; right: string }) => (
  <div className='flex items-center gap-2.5 flex-wrap'>
    <Chip
      size='sm'
      variant='flat'
      classNames={{
        base: 'bg-cherry-50 dark:bg-cherry-950',
        content: 'text-cherry-700 dark:text-cherry-300 text-[11px] font-semibold uppercase tracking-wider',
      }}
    >
      {badge}
    </Chip>
    <span className='text-base font-bold'>{title}</span>
    <span className='tabular-nums text-xs text-default-500 ml-auto'>{right}</span>
  </div>
);

const InsightNote = ({ children }: { children: React.ReactNode }) => (
  <div className='mt-3.5 px-3.5 py-3 bg-danger-50/60 dark:bg-danger-50/10 border-l-2 border-primary text-[13px] text-default-600 rounded-r-lg'>
    {children}
  </div>
);

const numClass = (kind: 'none' | 'bad' | 'worst') => (kind === 'worst' ? 'font-semibold' : '');
const numStyle = (kind: 'none' | 'bad' | 'worst') =>
  kind === 'worst' ? { color: STATUS_COLORS.critico } : kind === 'bad' ? { color: STATUS_COLORS.leve } : undefined;

/* ---------- filtros de cohorte (grilla de selects, como Retail) ---------- */

const FILTER_DEFS = [
  { label: 'Variedad', options: ['Todas (7)', 'Santina', 'Lapins', 'Regina', 'Royal Dawn', 'Kordia', 'Bing', 'Sweetheart'] },
  { label: 'Destino', options: ['China', 'Europa', 'Corea del Sur', 'EE.UU. Este', 'EE.UU.', 'Todos'] },
  { label: 'Vía', options: ['Marítimo', 'Aéreo', 'Todas'] },
  { label: 'Semanas embalaje', options: ['50–52', '45–48', '49–52', '01–04', 'Todas'] },
];

export const FiltersPanel = () => {
  const [values, setValues] = useState(FILTER_DEFS.map((f) => f.options[0]));

  return (
    <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
      <div className='space-y-4'>
        <p className='text-sm text-default-500'>Cohorte de comparación</p>
        {FILTER_DEFS.map((f, i) => (
          <Select
            key={f.label}
            label={f.label}
            size='sm'
            className='w-full'
            selectedKeys={new Set([values[i]])}
            onChange={(e) => {
              if (!e.target.value) return;
              setValues((v) => v.map((x, j) => (j === i ? e.target.value : x)));
            }}
          >
            {f.options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </Select>
        ))}

        <div>
          <p className='text-sm text-default-500 mb-2'>Cierre del índice</p>
          <div className='flex flex-col gap-1.5'>
            {COHORT.methods.map((m) => {
              const btn = (
                <button
                  type='button'
                  key={m.key}
                  disabled={!m.enabled}
                  className={`w-full text-left px-3 py-2 text-[13px] rounded-lg border ${
                    m.enabled
                      ? 'bg-brand-900 text-white border-brand-900'
                      : 'bg-default-50 text-default-400 border-default-200 cursor-not-allowed'
                  }`}
                >
                  {m.name}
                  <span className='block text-[11px] opacity-75 mt-px'>{m.coverage}</span>
                </button>
              );
              return m.enabled ? (
                btn
              ) : (
                <Tooltip key={m.key} content='Se necesitan 5 exportadoras reportando venta real.'>
                  {btn}
                </Tooltip>
              );
            })}
          </div>
        </div>

        <p className='text-xs text-default-400 pt-2 border-t border-default-100'>
          <span className='font-semibold' style={{ color: STATUS_COLORS.holgado }}>
            N = {COHORT.n} ✓
          </span>{' '}
          {COHORT.note}
        </p>
      </div>
    </div>
  );
};

/* ---------- tab Resumen ---------- */

const RuleCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <CardHead
        title='Dónde se consume la vida útil'
        meta='ponderado por kilos · vida útil por variedad (tabla v3)'
        sub={`Escala en % de vida útil, porque cada variedad tiene la suya. Vida útil ponderada de la cohorte: ${RULE.totalDays} días.`}
      />

      <div className='grid grid-cols-2 gap-3 md:grid-cols-4 my-4'>
        <StatTile label='Vida útil consumida' value={`${RULE.consumedPct} · ${RULE.consumedDays} de ${RULE.totalDays} d`} hint='al desconsolidar' />
        <StatTile label='Tu índice' value={RULE.yourIndex} />
        <StatTile label='Industria' value={RULE.industryIndex} />
        <StatTile label='Remanente al llegar' value={`${RULE.yourRemaining} · ind. ${RULE.industryRemaining}`} />
      </div>

      <div className='flex items-stretch'>
        <div
          className='w-[118px] max-md:w-[88px] shrink-0 border border-dashed border-default-300 border-r-0 rounded-l-lg px-2.5 py-2'
          style={{ background: 'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(127,127,127,0.09) 5px, rgba(127,127,127,0.09) 10px)' }}
        >
          <p className='tabular-nums text-[15px] font-semibold'>{RULE.pre.days}</p>
          <p className='text-[10.5px] text-default-500 leading-tight mt-0.5'>
            {RULE.pre.note}
            <br />
            {RULE.pre.sub}
          </p>
        </div>
        <div className='flex-1 flex relative min-h-[64px] border border-default-300 rounded-r-lg overflow-hidden'>
          {RULE.segments.map((s, i) => (
            <div
              key={s.key}
              className='relative flex items-end px-2 py-1.5 min-w-0 border-l first:border-l-0 border-white/50'
              style={{ width: `${s.pct}%`, background: STAGE_COLORS[i], color: i >= 3 ? '#fff' : '#881337' }}
            >
              <span className='absolute top-1.5 left-2 max-md:hidden text-[10px] font-semibold uppercase tracking-wider opacity-90 whitespace-nowrap'>{s.key}</span>
              <span className='tabular-nums text-[13px] max-md:text-[11px] font-semibold whitespace-nowrap'>{s.days}</span>
            </div>
          ))}
          <div className='absolute top-0 bottom-0 w-0 border-l-2 border-dotted border-foreground z-[3]' style={{ left: `${RULE.industryMarkPct}%` }}>
            <span className='absolute -top-px left-1 tabular-nums text-[10px] bg-foreground text-background px-1.5 py-0.5 rounded whitespace-nowrap'>
              industria 94%
            </span>
          </div>
        </div>
      </div>

      <div className='relative h-[26px] ml-[118px] max-md:ml-[88px]'>
        {[0, 20, 40, 60, 80, 100].map((p) => (
          <div key={p} className='absolute top-0 -translate-x-1/2 text-center' style={{ left: `${p}%` }}>
            <i className='block w-px h-1.5 bg-default-300 mx-auto' />
            <span className='tabular-nums text-[10.5px] text-default-400 block mt-0.5'>{p}%</span>
          </div>
        ))}
      </div>
      <p className='ml-[118px] max-md:ml-[88px] text-[11.5px] text-default-400 -mt-0.5'>% de vida útil consumida</p>

      <div className='flex gap-4 flex-wrap mt-4 pt-3.5 border-t border-default-100'>
        {RULE.segments.map((s, i) => (
          <div key={s.key} className='flex items-center gap-1.5 text-[12.5px] text-default-500'>
            <i className='w-[11px] h-[11px] rounded-[2px] shrink-0' style={{ background: STAGE_COLORS[i] }} />
            {s.legend}
          </div>
        ))}
      </div>
    </CardBody>
  </Card>
);

const DistributionCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-0 pt-5'>
      <div className='px-5'>
        <CardHead
          title='Cómo llegó tu volumen'
          meta='sobre volumen embarcado, no vendido'
          sub='Índice = edad de la caja ÷ vida útil de la variedad.'
        />
        <div className='flex h-[52px] border border-default-300 rounded-lg overflow-hidden mt-3'>
          {DISTRIBUTION.map((d) => (
            <div
              key={d.name}
              className='flex items-center justify-center tabular-nums text-sm font-semibold min-w-0 border-l first:border-l-0 border-white/50'
              style={{ width: `${d.you}%`, background: d.color, color: d.dark ? '#fff' : '#2A1B00' }}
            >
              {d.you >= 5 ? `${d.you}%` : ''}
            </div>
          ))}
        </div>
      </div>
      <div className='grid grid-cols-2 md:grid-cols-5 gap-px bg-default-200 border-t border-default-200 mt-5'>
        {DISTRIBUTION.map((d) => (
          <div key={d.name} className='bg-content1 px-3 py-2.5'>
            <div className='w-6 h-[3px] rounded mb-1.5' style={{ background: d.color }} />
            <p className='text-[13px] font-semibold'>{d.name}</p>
            <p className='tabular-nums text-[11px] text-default-400 mt-px'>{d.range}</p>
            <p className='tabular-nums text-[17px] mt-1'>
              {d.you}% <span className='text-[11px] text-default-400'>· ind. {d.industry}%</span>
            </p>
          </div>
        ))}
      </div>
    </CardBody>
  </Card>
);

export const ResumenTab = () => (
  <div className='flex flex-col gap-4'>
    <RuleCard />
    <DistributionCard />
  </div>
);

/* ---------- tabs de cortes ---------- */

export const DestinosTab = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <CardHead
        title='Índice de frescura por destino'
        meta='cada destino contra su propia industria'
        sub='Un destino más lejano consume más vida útil, y eso no es una ineficiencia: es la decisión comercial. Aéreo y marítimo van separados aunque sea el mismo país.'
      />
      {DESTINATIONS.map((d) => (
        <PercRowLine key={`${d.name}-${d.tag?.label}`} row={d} />
      ))}
      <AxisRow labels={['0,30', '0,60', '0,90', '1,20']} spread />
      <ChartNote>{DESTINATIONS_NOTE}</ChartNote>
    </CardBody>
  </Card>
);

export const SemanaTab = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <CardHead
        title='Índice de frescura por semana de embalaje'
        meta='tu mediana · banda P25–P75 industria'
        sub='Las barras grises son tu volumen de la semana. Un índice malo en una semana con 2% del embarque no es un problema; en la semana 51, con el 22%, sí lo es.'
      />
      <WeekIndexChart />
      <ChartNote>{WEEK_CHART.note}</ChartNote>
    </CardBody>
  </Card>
);

export const VariedadesTab = () => {
  const [weekFilter, setWeekFilter] = useState(VARIETY_WEEK_CHIPS[0]);
  const varieties =
    weekFilter === VARIETY_WEEK_CHIPS[0]
      ? VARIETIES
      : VARIETIES.filter((v) => v.weeksKey.includes(weekFilter));

  return (
    <Card shadow='sm'>
      <CardBody className='p-5'>
        <CardHead
          title='Índice de frescura por variedad'
          meta='ordenado por brecha vs. industria'
          sub='Comparar días entre variedades no significa nada; comparar el índice sí, porque ya está normalizado por la vida útil de cada una.'
        />
        <div className='flex gap-1.5 flex-wrap my-3'>
          {VARIETY_WEEK_CHIPS.map((c) => (
            <Chip
              key={c}
              size='sm'
              variant={weekFilter === c ? 'solid' : 'bordered'}
              classNames={{
                base: `cursor-pointer ${weekFilter === c ? 'bg-brand-900' : 'border-default-200'}`,
                content: `text-xs ${weekFilter === c ? 'text-white' : 'text-default-500'}`,
              }}
              onClick={() => setWeekFilter(c)}
            >
              {c}
            </Chip>
          ))}
        </div>
        {varieties.map((v) => (
          <PercRowLine key={v.name} row={v} />
        ))}
        <ChartNote>{VARIETIES_NOTE}</ChartNote>
      </CardBody>
    </Card>
  );
};

/* ---------- tab Tramos y actores ---------- */

const StripsCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <CardHead
        title='Tu posición tramo por tramo'
        meta='banda P25–P75 industria · menos días es mejor'
        sub='Cada tramo se abre por el actor que lo controla en las secciones de abajo. Despacho → zarpe no se abre: depende de la distancia a puerto, no de un tercero.'
      />
      {STRIPS.map((s) => (
        <div key={s.name} className='py-3.5 border-b border-default-100 last:border-none last:pb-1'>
          <div className='flex justify-between items-baseline gap-3 mb-2 flex-wrap'>
            <span className='text-sm font-medium'>
              {s.name}
              {s.hint && <span className='text-default-400 font-normal max-md:hidden'> · {s.hint}</span>}
            </span>
            <span className='tabular-nums text-[12.5px] text-default-500'>
              tú <b className='text-foreground font-semibold'>{s.you}</b> · mediana {s.median}{' '}
              <Chip size='sm' variant='flat' color={s.good ? 'success' : 'warning'} classNames={{ content: 'text-[11px] tabular-nums' }}>
                {s.pill}
              </Chip>
            </span>
          </div>
          <PercTrack row={{ name: s.name, sub: '', iqrLeft: s.iqrLeft, iqrWidth: s.iqrWidth, median: s.med, you: s.youPos, youLabel: 'tú', gap: '' }} height={26} />
          <AxisRow labels={s.axis} />
        </div>
      ))}
    </CardBody>
  </Card>
);

const ZoomGridHead = ({ cols }: { cols: string[] }) => (
  <div className='grid grid-cols-[172px_52px_1fr_66px_58px] max-md:grid-cols-[112px_1fr_56px] gap-3 max-md:gap-2 items-center pt-3.5 pb-1.5 border-b border-default-200'>
    {cols.map((c, i) => (
      <span
        key={c || i}
        className={`text-[10px] font-semibold uppercase tracking-[0.11em] text-default-400 ${i >= 3 || i === 1 ? 'text-right' : ''} ${
          i === 1 || i === 3 ? 'max-md:hidden' : ''
        } ${i === 4 ? 'text-right' : ''}`}
      >
        {c}
      </span>
    ))}
  </div>
);

const PlantsCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <ZoomHead badge='Planta' title='Embalaje → despacho, por planta' right={`tu mediana ${PLANTS.yourMedian}`} />
      <p className='text-sm text-default-500 mt-2 max-w-[75ch]'>
        Las plantas propias solo se comparan entre sí y contra la mediana de industria. La franja rayada es el mínimo técnico de preenfriado: bajarla no es eficiencia, es fruta despachada sin llegar a temperatura.
      </p>
      <ZoomGridHead cols={['Planta', 'Tu vol.', 'Días en planta', 'Peak s51', 'vs ind.']} />
      {PLANTS.rows.map((r) => (
        <div key={r.name} className='grid grid-cols-[172px_52px_1fr_66px_58px] max-md:grid-cols-[112px_1fr_56px] gap-3 max-md:gap-2 items-center py-2.5 border-b border-default-100 last:border-none'>
          <div className='text-[13.5px] max-md:text-[12.5px] font-medium leading-tight'>
            {r.name}
            <TagChip tag={r.tag} />
            <span className='block tabular-nums text-[11px] text-default-400 font-normal mt-0.5'>{r.sub}</span>
          </div>
          <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.vol}</div>
          <div className='relative h-[22px] rounded-md border border-default-100 bg-default-50'>
            <div
              className='absolute top-0 bottom-0 left-0 z-0 rounded-l-md'
              style={{ width: `${PLANTS.floorPct}%`, background: 'repeating-linear-gradient(135deg,rgba(190,18,60,0.12),rgba(190,18,60,0.12) 3px,transparent 3px,transparent 7px)' }}
            />
            <div className='absolute top-1 bottom-1 left-0 rounded-[2px] z-[1]' style={{ width: `${r.barPct}%`, background: r.color }} />
            <div className='absolute -top-1 -bottom-1 w-0 border-l-2 border-dotted border-default-400 z-[3]' style={{ left: `${PLANTS.medianPct}%` }} />
            <div className='absolute -top-1 -bottom-1 w-[3px] bg-primary z-[2]' style={{ left: `${r.barPct}%` }}>
              <span className='absolute -top-[19px] left-1/2 -translate-x-1/2 tabular-nums text-[10.5px] bg-primary text-white px-1 rounded whitespace-nowrap'>{r.youLabel}</span>
            </div>
          </div>
          <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.peak}</div>
          <div className={`text-right tabular-nums text-[12.5px] ${numClass(r.gapKind)}`} style={numStyle(r.gapKind)}>{r.gap}</div>
        </div>
      ))}
      <AxisRow labels={PLANTS.axis} spread />
      <InsightNote>{PLANTS.note}</InsightNote>
    </CardBody>
  </Card>
);

const CarriersCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <ZoomHead badge='Naviera' title='Zarpe → arribo, por naviera y servicio' right={`tu mediana ${CARRIERS.yourMedian}`} />
      <p className='text-sm text-default-500 mt-2 max-w-[75ch]'>
        La barra clara es el rango P25–P75 de tránsito real del servicio en la temporada; la línea punteada es tu mediana ponderada. ATA−ETA se mide contra el ETA vigente al zarpe.
      </p>
      <ZoomGridHead cols={['Naviera · servicio', 'Tu vol.', 'Tránsito real', 'ATA−ETA', '±1 día']} />
      {CARRIERS.rows.map((r) => (
        <div key={r.name} className='grid grid-cols-[172px_52px_1fr_66px_58px] max-md:grid-cols-[112px_1fr_56px] gap-3 max-md:gap-2 items-center py-2.5 border-b border-default-100 last:border-none'>
          <div className='text-[13.5px] max-md:text-[12.5px] font-medium leading-tight'>
            {r.name}
            <TagChip tag={r.tag} />
            <span className='block tabular-nums text-[11px] text-default-400 font-normal mt-0.5'>{r.sub}</span>
          </div>
          <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.vol}</div>
          <div className='relative h-[22px] rounded-md border border-default-100 bg-default-50'>
            <div className='absolute top-[5px] bottom-[5px] bg-default-200 rounded-[2px]' style={{ left: `${r.iqrLeft}%`, width: `${r.iqrWidth}%` }} />
            <div className='absolute top-[2px] bottom-[2px] w-[2px] bg-default-400' style={{ left: `${r.median}%` }} />
            <div className='absolute -top-1 -bottom-1 w-0 border-l-2 border-dotted border-foreground z-[2]' style={{ left: `${CARRIERS.refPct}%` }} />
          </div>
          <div className={`text-right tabular-nums text-[12.5px] ${numClass(r.kind)}`} style={numStyle(r.kind)}>{r.ataEta}</div>
          <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.ontime}</div>
        </div>
      ))}
      <AxisRow labels={CARRIERS.axis} spread />
      <InsightNote>{CARRIERS.note}</InsightNote>
    </CardBody>
  </Card>
);

const ArrivalCard = () => (
  <Card shadow='sm'>
    <CardBody className='p-5'>
      <CardHead
        title='Arribo → desconsolidación, por semana de arribo'
        meta='desviación en días vs. mediana de la industria'
        sub='Centrado en la mediana semanal de la industria mide lo tuyo; la franja inferior conserva los días efectivos, que son los que entran al índice.'
      />
      <ArrivalDeviationChart />
      <ChartNote>{ARRIVAL_CHART.note}</ChartNote>

      <div className='mt-5 pt-4 border-t border-default-200'>
        <ZoomHead badge='Recibidor' title='Quién desconsolida más rápido' right={`mediana industria ${RECEIVERS.industryMedian}`} />
        <p className='text-sm text-default-500 mt-2 max-w-[75ch]'>
          Se mide desde el <b>arribo real (ATA)</b>: si la nave llega tarde, ese atraso es de la naviera y ya está medido arriba. La última columna suma ambos contra la fecha prometida.
        </p>
        <ZoomGridHead cols={['Recibidor', 'Tu vol.', 'ATA → desconsolidación', 'Nave', 'vs ETA']} />
        {RECEIVERS.rows.map((r) => (
          <div key={r.name} className='grid grid-cols-[172px_52px_1fr_66px_58px] max-md:grid-cols-[112px_1fr_56px] gap-3 max-md:gap-2 items-center py-2.5 border-b border-default-100 last:border-none'>
            <div className='text-[13.5px] max-md:text-[12.5px] font-medium leading-tight'>
              {r.name}
              <span className='block tabular-nums text-[11px] text-default-400 font-normal mt-0.5'>{r.sub}</span>
            </div>
            <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.vol}</div>
            <div className='relative h-[22px] rounded-md border border-default-100 bg-default-50'>
              <div className='absolute top-1 bottom-1 left-0 rounded-[2px] z-[1]' style={{ width: `${r.barPct}%`, background: r.color }} />
              <div className='absolute -top-1 -bottom-1 w-0 border-l-2 border-dotted border-default-400 z-[3]' style={{ left: `${RECEIVERS.medianPct}%` }} />
              <div className='absolute -top-1 -bottom-1 w-[3px] bg-primary z-[2]' style={{ left: `${r.barPct}%` }}>
                <span className='absolute -top-[19px] left-1/2 -translate-x-1/2 tabular-nums text-[10.5px] bg-primary text-white px-1 rounded whitespace-nowrap'>{r.youLabel}</span>
              </div>
            </div>
            <div className='text-right tabular-nums text-[12.5px] max-md:hidden'>{r.vessel}</div>
            <div className={`text-right tabular-nums text-[12.5px] ${numClass(r.kind)}`} style={numStyle(r.kind)}>{r.total}</div>
          </div>
        ))}
        <AxisRow labels={RECEIVERS.axis} spread />
        <InsightNote>{RECEIVERS.note}</InsightNote>
      </div>
    </CardBody>
  </Card>
);

export const TramosTab = () => (
  <div className='flex flex-col gap-4'>
    <StripsCard />
    <PlantsCard />
    <CarriersCard />
    <ArrivalCard />
  </div>
);

export const MethodologyGrid = () => (
  <Card shadow='sm' className='mt-4'>
    <CardBody className='p-5'>
      <h3 className='text-sm font-bold mb-3'>Metodología</h3>
      <div className='grid gap-x-6 gap-y-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'>
        {METHODOLOGY.map((m) => (
          <div key={m.k}>
            <p className='text-xs font-medium text-default-400'>{m.k}</p>
            <p className='text-[13px] text-default-600 mt-0.5'>{m.v}</p>
          </div>
        ))}
      </div>
    </CardBody>
  </Card>
);
